import type {
  BillType,
  ChargeType,
  PayorType,
  TypeOfBill,
} from '../services/api/types.gen';

// Charge/unit basis labels shared by the insurance modal and the per-visit
// rates editor. CONSTANT (a fixed unit count) is valid on rate rows but not
// as an insurance-level charge type, so the two option lists differ.

export const CHARGE_TYPE_LABELS: Record<ChargeType, string> = {
  VISIT: 'Per visit',
  HOURLY: 'Hourly',
  FIFTEEN_MINUTE: '15 min',
  CONSTANT: 'Constant',
};

// Insurance-level charge type (the backend rejects CONSTANT here).
export const INSURANCE_CHARGE_TYPE_OPTIONS: {
  value: ChargeType;
  label: string;
}[] = [
  { value: 'VISIT', label: 'Per visit' },
  { value: 'HOURLY', label: 'Hourly' },
  { value: 'FIFTEEN_MINUTE', label: '15-minute increment' },
];

// Per-visit rate row charge/allowed bases. CONSTANT is deliberately absent:
// it is a claim-units concept only — the WellSky sync never emits it for
// charge_by/allowed_by and the backend rejects it there with a 422.
export const RATE_CHARGE_BASIS_OPTIONS: {
  value: ChargeType;
  label: string;
}[] = [
  { value: 'VISIT', label: CHARGE_TYPE_LABELS.VISIT },
  { value: 'HOURLY', label: CHARGE_TYPE_LABELS.HOURLY },
  { value: 'FIFTEEN_MINUTE', label: CHARGE_TYPE_LABELS.FIFTEEN_MINUTE },
];

// Claim-units basis: the full enum. CONSTANT (a fixed unit count per claim
// line) is valid here and pairs with the constant-units input.
export const CLAIM_UNITS_BASIS_OPTIONS: {
  value: ChargeType;
  label: string;
}[] = (Object.entries(CHARGE_TYPE_LABELS) as [ChargeType, string][]).map(
  ([value, label]) => ({ value, label })
);

// UB-04 FL 4 scheme. 329 = every period is a final claim (Medicare
// PDGM behavior, same as leaving the field unset). 32X = payer-specific
// interim series. 33X/34X are stored for WellSky scrape fidelity but claim
// generation does not support them yet — worksheets fall back to 0329.
// Record<TypeOfBill, string> so adding a backend enum value breaks the build
// here until it gets a label (same exhaustiveness idiom as CHARGE_TYPE_LABELS).
export const TYPE_OF_BILL_LABELS: Record<TypeOfBill, string> = {
  TOB_329: '329 — Final claim',
  TOB_32X: '32X — Interim series (321/322/323/324)',
  TOB_33X: '33X — Not yet supported for claims',
  TOB_34X: '34X — Not yet supported for claims',
};

// Radix Select items cannot carry an empty-string value, so the explicit
// "clear back to default" option uses a sentinel the modal maps to null
// (the backend documents null as "restore the default 329 behavior").
export const TYPE_OF_BILL_DEFAULT_OPTION_VALUE = 'DEFAULT';

// 33X/34X stay visible (a WellSky-synced value still displays) but disabled:
// claim generation does not support those families yet, so offering them for
// manual selection only invites misconfiguration.
export const TYPE_OF_BILL_OPTIONS: {
  value: TypeOfBill | typeof TYPE_OF_BILL_DEFAULT_OPTION_VALUE;
  label: string;
  disabled?: boolean;
}[] = [
  {
    value: TYPE_OF_BILL_DEFAULT_OPTION_VALUE,
    label: 'Default (329 — Final claim)',
  },
  ...(Object.entries(TYPE_OF_BILL_LABELS) as [TypeOfBill, string][]).map(
    ([value, label]) => ({
      value,
      label,
      disabled: value === 'TOB_33X' || value === 'TOB_34X',
    })
  ),
];

export const BILLING_FREQUENCY_OPTIONS: { value: string; label: string }[] = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'SEMIMONTHLY', label: 'Semi-monthly' },
  { value: 'EPISODIC', label: 'Episodic' },
];

// The per-insurance readiness skips: the one list every form, payload and
// request type is built from, so a new skip is added here and in the switches.
// HIPPS has none: the PDGM setting decides whether its check applies.
export const READINESS_SKIP_FIELDS = [
  'skip_visits_readiness',
  'skip_orders_readiness',
  'skip_payment_oasis_readiness',
  'skip_authorization_readiness',
] as const;

export type ReadinessSkipField = (typeof READINESS_SKIP_FIELDS)[number];

export type ReadinessSkipFlags = Record<ReadinessSkipField, boolean>;

export const NO_READINESS_SKIPS: ReadinessSkipFlags = {
  skip_visits_readiness: false,
  skip_orders_readiness: false,
  skip_payment_oasis_readiness: false,
  skip_authorization_readiness: false,
};

export const READINESS_SKIP_SWITCHES: {
  field: ReadinessSkipField;
  label: string;
}[] = [
  { field: 'skip_visits_readiness', label: 'Skip visits check' },
  { field: 'skip_orders_readiness', label: 'Skip orders check' },
  {
    field: 'skip_payment_oasis_readiness',
    label: 'Skip payment OASIS export',
  },
  { field: 'skip_authorization_readiness', label: 'Skip authorization check' },
];

// How the agency bills an insurance.
export const DEFAULT_BILL_TYPE: BillType = 'UB04';

export const BILL_TYPE_OPTIONS: { value: BillType; label: string }[] = [
  { value: DEFAULT_BILL_TYPE, label: 'UB-04 (clearinghouse)' },
  { value: 'INVOICE', label: 'Invoice' },
];

export const isInvoiceBilled = (billType: BillType | null | undefined) =>
  billType === 'INVOICE';

// Traditional Medicare is always billed by UB-04, whatever the form holds.
export const effectiveBillType = (
  billType: BillType,
  payorType: PayorType | null
): BillType =>
  payorType === 'MEDICARE_TRADITIONAL' ? DEFAULT_BILL_TYPE : billType;
